import { NICKNAME_MESSAGES, nicknameProblem, type ChallengeInfo } from '@cruz/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { load, save } from '../lib/storage.ts';
import { api, ApiError, errorMessage } from './api.ts';
import { loadGame, saveGame, type DeviceGame } from './device.ts';
import { PlayScreen } from './PlayScreen.tsx';
import { ResultsScreen } from './ResultsScreen.tsx';
import {
  inputClass,
  Leaderboard,
  Notice,
  primaryButton,
  secondaryButton,
  ShareButtons,
} from './ui.tsx';

type View = 'landing' | 'playing' | 'results';

const days = (iso: string, nowIso: string) =>
  Math.max(0, Math.ceil((Date.parse(iso) - Date.parse(nowIso)) / 86_400_000));

/**
 * /reto/{code}: who made it, how it works and the leaderboard; join with a nickname;
 * then play and see the results. A device that already joined goes back to its game.
 */
export function ChallengeScreen({ code }: { code: string }) {
  const [info, setInfo] = useState<ChallengeInfo | null>(null);
  const [game, setGame] = useState<DeviceGame | null>(() => loadGame(code));
  const [view, setView] = useState<View>('landing');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .info(code, game?.token, { retries: 6 })
      .then((i) => {
        if (!live) return;
        setInfo(i);
        setError(null);
        if (game && i.me === null) {
          // The token is not valid for this challenge any more: start afresh
          setGame(null);
        } else if (i.me?.state === 'finished') {
          setView('results');
        } else if (i.me?.state === 'playing') {
          // The game clock is running: straight back to the question
          setView('playing');
        }
      })
      .catch((err: unknown) => {
        if (live) setError(errorMessage(err));
      });
    return () => {
      live = false;
    };
  }, [code, game]);

  const onFinished = useCallback(() => setView('results'), []);

  if (error && !info) {
    return (
      <div className="space-y-4">
        <Notice tone="error">{error}</Notice>
        <a href="/reto/nuevo" className={secondaryButton}>
          Crear un reto nuevo
        </a>
      </div>
    );
  }
  if (!info) {
    return (
      <p className="py-10 text-center text-lg text-ink-soft dark:text-cream/75">
        Cargando el reto…
      </p>
    );
  }
  if (view === 'playing' && game) {
    return <PlayScreen code={code} token={game.token} onFinished={onFinished} />;
  }
  if (view === 'results' && game) {
    return <ResultsScreen code={code} token={game.token} creator={info.creator} />;
  }

  const closed = info.status === 'closed';
  return (
    <div className="space-y-7">
      <section>
        <h1 className="font-serif text-3xl leading-snug text-purple dark:text-cream">
          {game?.nickname === info.creator && info.me?.is_creator
            ? 'Tu reto está listo'
            : `${info.creator} te reta`}
        </h1>
        <p className="mt-2 text-lg text-ink-soft dark:text-cream/80">
          {info.question_count} preguntas sobre la Semana Santa de Sevilla,{' '}
          {info.seconds_per_question} segundos para cada una. Datos oficiales de {info.data_year}.
        </p>
        <p className="mt-1 text-base text-ink-soft dark:text-cream/70">
          {closed
            ? 'Este reto ya ha terminado: la clasificación es definitiva.'
            : `Se puede jugar ${days(info.expires_at, info.server_now) <= 1 ? 'hasta mañana' : `durante ${days(info.expires_at, info.server_now)} días más`}. No se puede volver atrás ni pausar.`}
        </p>
      </section>

      {!closed && game && info.me?.state === 'not_started' && (
        <section className="space-y-3">
          {info.me.is_creator && (
            <>
              <h2 className="font-serif text-2xl">Compártelo con tus amigos</h2>
              <ShareButtons code={code} creator={info.creator} />
            </>
          )}
          <button
            type="button"
            className={info.me.is_creator ? secondaryButton : primaryButton}
            onClick={() => setView('playing')}
          >
            {info.me.is_creator ? 'Jugar yo ahora' : 'Empezar'}
          </button>
        </section>
      )}

      {!closed && !game && (
        <JoinForm
          code={code}
          onJoined={(g) => {
            saveGame(code, g);
            setGame(g);
            setView('playing');
          }}
        />
      )}

      <section>
        <h2 className="mb-3 font-serif text-2xl text-purple dark:text-cream">Clasificación</h2>
        <Leaderboard entries={info.leaderboard} playing={info.playing} me={game?.nickname} />
      </section>

      {closed && (
        <a href="/reto/nuevo" className={primaryButton}>
          Crear un reto nuevo
        </a>
      )}
    </div>
  );
}

function JoinForm({ code, onJoined }: { code: string; onJoined: (g: DeviceGame) => void }) {
  const [nickname, setNickname] = useState(() => load('cruz.reto.nickname', ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = nickname.trim() ? nicknameProblem(nickname) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const p = nicknameProblem(nickname);
    if (p) return setError(NICKNAME_MESSAGES[p]);
    setBusy(true);
    setError(null);
    try {
      const r = await api.join(code, nickname);
      save('cruz.reto.nickname', r.nickname);
      onJoined({ token: r.token, nickname: r.nickname });
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.error === 'nickname_invalid' && err.body.reason
          ? NICKNAME_MESSAGES[err.body.reason]
          : errorMessage(err),
      );
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3">
      <label htmlFor="join-nickname" className="block text-lg font-semibold">
        Tu apodo
      </label>
      <input
        id="join-nickname"
        className={inputClass}
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        maxLength={40}
        autoComplete="nickname"
        enterKeyHint="go"
        aria-invalid={problem != null}
        aria-describedby="join-help"
      />
      <p id="join-help" className="text-sm text-ink-soft dark:text-cream/70">
        {problem ? NICKNAME_MESSAGES[problem] : 'Sin registro. Una partida por móvil.'}
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      <button type="submit" className={primaryButton} disabled={busy}>
        {busy ? 'Entrando…' : 'Aceptar el reto'}
      </button>
    </form>
  );
}
